"use client"
import { useCurrencies } from "@opensea/app-kit"
import type { FragmentOf } from "@opensea/graphql"
import { readFragment } from "@opensea/graphql"
import type { NumberFormatDisplay } from "@opensea/i18n"
import { type CurrencyPreference, useCurrency } from "@opensea/navigation"
import { NumberDisplay } from "@opensea/ui-kit"
import { type Nullable, UnreachableCaseError } from "@opensea/utils"
import { getCurrencyIdentifier } from "../currencies/utils"
import { NativePriceFragment } from "./NativePrice.fragment"
import type { Conversion } from "./types"
import { UsdPrice } from "./UsdPrice"

type Props = {
  price: Nullable<FragmentOf<typeof NativePriceFragment>>
  display?: NumberFormatDisplay
  conversion?: Conversion
  multiplier?: number
  currency?: CurrencyPreference
}

export function NativePrice({
  price: priceKey,
  display = "compact",
  conversion = "current",
  multiplier = 1,
  currency: propCurrency,
}: Props) {
  const price = readFragment(NativePriceFragment, priceKey)

  const { currency: contextCurrency } = useCurrency()
  const currency = propCurrency ?? contextCurrency

  const { getNativePrice } = useCurrencies()

  switch (currency) {
    case "crypto":
      if (!price) {
        return <NumberDisplay />
      }

      if (conversion === "historical") {
        return (
          <NumberDisplay
            display={display}
            suffix={price.native.symbol}
            value={price.native.unit * multiplier}
          />
        )
      }

      // Native price is calculated from the conversion rates of token & native currency
      return (
        <NumberDisplay
          display={display}
          suffix={price.native.symbol}
          value={
            getNativePrice(
              getCurrencyIdentifier(price.token),
              getCurrencyIdentifier(price.native),
              price.token.unit * multiplier,
            ) ?? price.native.unit * multiplier
          }
        />
      )

    case "usd":
      return (
        <UsdPrice
          conversion={conversion}
          multiplier={multiplier}
          price={price}
        />
      )
    default:
      throw new UnreachableCaseError(currency)
  }
}
